import * as cx from 'classnames'
import format from 'date-fns/esm/format'
import get from 'lodash-es/get'
import * as React from 'react'

import ui from '../../../../../common/constants/ui'
import { ILocationParams } from '../../../../../common/reducers/reactRouterLocation'
import { shortenNumber } from '../../../../../common/utils/formatString'
import { IDecoratedItem } from '../../../../../typings/searchResults'
import EnhancedLazyLoad from '../../../../shared/components/enhancedLazyLoad'
import TrendingSash from '../../../../shared/components/trendingSash'
import CartButtons from './cartButtons'
import FavButtons from './favButtons'
import Features from './features'
import Header from './header'
import Preview from './preview'
import Price from './price'
import Rating from './rating'
import ShowMore from './showMore'
import Tags from './tags'

import * as styles from './defaultItemCard.module.css'

interface IProps extends IDecoratedItem {
  locationParams: ILocationParams
  viewType: string
}

interface IState {
  collapsed: boolean
}

class ItemCard extends React.Component<IProps, IState> {
  constructor(props: IProps) {
    super(props)
    this.state = { collapsed: true }
    this.toggleCollapsed = this.toggleCollapsed.bind(this)
  }

  public render() {
    const { collapsed } = this.state
    const {
      item,
      viewType,
      rank,
      onItemLinkClick,
      onAddToCart,
      onAddToCollections,
      onAddToFavourites,
      locationParams
    } = this.props
    const isList = viewType === 'list'
    const stylesViewType = isList ? styles.isList : styles.isGrid
    const stylesIsShowingLess = collapsed ? styles.showingLess : null
    const { tags = '', term = '' } = locationParams
    const numSales = get(item, 'number_of_sales')
    const keyFeatures = get(item, 'key_features', [])
    const showKeyFeatures = !!keyFeatures && keyFeatures.length > 0
    const ratingCount = get(item, 'rating.count')

    return (
      <li className={cx(styles.root, stylesViewType)}>
        <article className={styles.card}>
          <section className={cx(styles.preview, stylesViewType)}>
            {item.trending && (
              <EnhancedLazyLoad height="100%" once offset={ui.lazyLoadOffset}>
                <TrendingSash large />
              </EnhancedLazyLoad>
            )}
            <Preview
              item={item}
              rank={rank}
              onItemLinkClick={onItemLinkClick}
            />
            <div className={cx(styles.previewTags, stylesViewType)}>
              <Tags item={item} tags={tags} term={term} />
            </div>
          </section>

          <section className={cx(styles.header, stylesViewType)}>
            <Header
              item={item}
              rank={rank}
              term={term}
              onItemLinkClick={onItemLinkClick}
              showCategory={isList}
            />
          </section>

          <div className={cx(styles.internalBorder, stylesViewType)} />

          <section className={cx(styles.salesInfo, stylesViewType)}>
            <span className={cx(styles.price, stylesViewType)}>
              <Price amount={item.ui.price} pricePeriod={item.ui.pricePeriod} />
            </span>

            {ratingCount >= 3 && (
              <span className={cx(styles.rating, stylesViewType)}>
                <Rating item={item} />
              </span>
            )}

            {!!numSales && (
              <span
                className={cx(
                  styles.sales,
                  stylesViewType,
                  stylesIsShowingLess
                )}
              >
                <span>{shortenNumber(numSales)} Sales</span>
              </span>
            )}

            <span
              className={cx(
                styles.updatedAt,
                stylesViewType,
                stylesIsShowingLess
              )}
            >
              Last updated: {format(item.updated_at, 'd MMM yy')}
            </span>
          </section>

          {showKeyFeatures && (
            <section
              className={cx(
                styles.keyFeatures,
                stylesViewType,
                stylesIsShowingLess
              )}
            >
              <Features term={term} keyFeatures={keyFeatures} />
            </section>
          )}

          <section
            className={cx(
              styles.standAloneTags,
              stylesViewType,
              stylesIsShowingLess
            )}
          >
            <Tags item={item} tags={tags} term={term} expandable={false} />
          </section>

          <section className={styles.showMore}>
            <ShowMore
              toggleCollapsed={this.toggleCollapsed}
              collapsed={this.state.collapsed}
            />
          </section>

          <section className={cx(styles.cartButtons, stylesViewType)}>
            <CartButtons item={item} onAddToCart={onAddToCart} />
          </section>

          <section className={cx(styles.favButtons, stylesViewType)}>
            <FavButtons
              item={item}
              onAddToCollections={onAddToCollections}
              onAddToFavourites={onAddToFavourites}
            />
          </section>
        </article>
      </li>
    )
  }

  private toggleCollapsed() {
    this.setState({ collapsed: !this.state.collapsed })
  }
}

export default ItemCard
